import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, Check, Loader2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { joinWaitlist } from "@/lib/api";

const INTERESTS = [
  "On-demand desk",
  "Long-term desk",
  "Meeting rooms",
  "Café membership",
  "Events & community",
  "Just curious",
];

export const WaitlistForm = ({ tone = "light" }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", interest: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const line =
    tone === "dark"
      ? "border-b border-cream/25 focus:border-cream text-cream placeholder:text-cream/40"
      : "border-b border-espresso/20 focus:border-espresso text-espresso placeholder:text-stone/60";

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone || !form.interest) {
      toast.error("Please fill in your name, email, phone and interest.");
      return;
    }
    setLoading(true);
    try {
      await joinWaitlist(form);
      setDone(true);
      toast.success("You're on the list.");
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputBase = `w-full bg-transparent py-3 text-base font-body outline-none transition-colors duration-300 ${line}`;

  return (
    <AnimatePresence mode="wait">
      {done ? (
        <motion.div
          key="success"
          data-testid="waitlist-success"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="py-10"
        >
          <div className={`mb-6 flex h-14 w-14 items-center justify-center rounded-full ${tone === "dark" ? "bg-cream text-espresso" : "bg-espresso text-cream"}`}>
            <Check className="h-6 w-6" strokeWidth={1.5} />
          </div>
          <h3 className={`font-display text-4xl font-light leading-none tracking-tight ${tone === "dark" ? "text-cream" : "text-espresso"}`}>
            Thank you, {form.name.split(" ")[0]}.
          </h3>
          <p className={`mt-4 max-w-md font-body text-base font-light ${tone === "dark" ? "text-cream/70" : "text-stone"}`}>
            You're on the list for The Upper Room. We'll be in touch as we prepare
            to open our doors in Johor — with first access, founding-member perks
            and a cup of something exceptional.
          </p>
        </motion.div>
      ) : (
        <motion.form
          key="form"
          data-testid="waitlist-form"
          onSubmit={submit}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="space-y-8"
        >
          <div className="grid gap-8 sm:grid-cols-2">
            <input
              data-testid="waitlist-name"
              className={inputBase}
              placeholder="Full name"
              value={form.name}
              onChange={update("name")}
            />
            <input
              data-testid="waitlist-phone"
              className={inputBase}
              placeholder="Phone number"
              value={form.phone}
              onChange={update("phone")}
            />
          </div>
          <input
            data-testid="waitlist-email"
            type="email"
            className={inputBase}
            placeholder="Email address"
            value={form.email}
            onChange={update("email")}
          />

          <Select value={form.interest} onValueChange={(v) => setForm((f) => ({ ...f, interest: v }))}>
            <SelectTrigger
              data-testid="waitlist-interest"
              className={`h-auto rounded-none border-0 bg-transparent px-0 py-3 text-base shadow-none focus:ring-0 ${line} ${form.interest ? "" : (tone === "dark" ? "text-cream/40" : "text-stone/60")}`}
            >
              <SelectValue placeholder="What are you most interested in?" />
            </SelectTrigger>
            <SelectContent className="rounded-none border-espresso/15 bg-cream">
              {INTERESTS.map((it) => (
                <SelectItem key={it} value={it} data-testid={`interest-${it.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  {it}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <textarea
            data-testid="waitlist-message"
            rows={1}
            className={`${inputBase} resize-none`}
            placeholder="Anything you'd like us to know? (optional)"
            value={form.message}
            onChange={update("message")}
          />

          <motion.button
            data-testid="waitlist-submit"
            type="submit"
            disabled={loading}
            whileTap={{ scale: 0.97 }}
            className={`group inline-flex items-center gap-3 rounded-full px-8 py-4 font-body text-sm font-semibold uppercase tracking-[0.18em] transition-colors duration-300 disabled:opacity-60 ${
              tone === "dark"
                ? "bg-cream text-espresso hover:bg-oak"
                : "bg-espresso text-cream hover:bg-[hsl(20_12%_25%)]"
            }`}
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Sending
              </>
            ) : (
              <>
                Register interest
                <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
              </>
            )}
          </motion.button>
        </motion.form>
      )}
    </AnimatePresence>
  );
};

export default WaitlistForm;
